import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import JsSIP from 'jssip'
import { createWidgetSocket } from '@kc3/chat-widget'
import { buildIceServers } from './iceServers.js'
import { attachIceFailureGuard } from './ice-failure-guard.js'
import { clientApi } from '../../api/client-portal.js'

export type ClientCallState =
  | 'IDLE'
  | 'REQUESTING_PERMISSION'
  | 'CONNECTING'
  | 'RINGING'
  | 'CONNECTED'
  | 'ENDING'
  | 'ENDED'
  | 'FAILED'

export type ClientCallErrorCode =
  | 'NO_AGENTS'
  | 'MIC_DENIED'
  | 'WS_UNREACHABLE'
  | 'REGISTER_FAILED'
  | 'TIMEOUT'
  | 'AGENT_REJECTED'
  | 'UNEXPECTED_TERMINATION'
  | 'UNSUPPORTED_BROWSER'
  | 'CALL_UNAVAILABLE'

/**
 * Maps a /call/session failure's server-reported `error` code to the
 * ClientCallErrorCode shown to the visitor. Anything not recognized here
 * (including a genuine network failure, which has no server body at all)
 * falls back to WS_UNREACHABLE — that's the only case it should actually
 * mean "unreachable".
 */
function callSessionErrorCode(err: unknown): ClientCallErrorCode {
  const body = (err as { body?: unknown } | undefined)?.body
  const serverError = body && typeof body === 'object' && 'error' in body ? (body as { error: unknown }).error : undefined
  if (serverError === 'NO_QUEUE_CONFIGURED' || serverError === 'CALL_DISABLED' || serverError === 'NOT_FOUND') {
    return 'CALL_UNAVAILABLE'
  }
  return 'WS_UNREACHABLE'
}

interface CallSessionResponse {
  callSessionId: string
  sipUsername: string
  password: string
  wsUrl: string
  sipDomain: string
  dialTarget: string
  expiresAt: string
  token: string
}

export interface UseClientCallResult {
  state: ClientCallState
  errorCode: ClientCallErrorCode | null
  durationSeconds: number
  muted: boolean
  /** Attach to an <audio autoPlay> element so the agent's voice is heard. */
  remoteAudioRef: RefObject<HTMLAudioElement | null>
  callSessionId: string | null
  startCall: () => Promise<void>
  endCall: () => void
  toggleMute: () => void
  retry: () => Promise<void>
}

/**
 * Minimal JsSIP session/UA shapes, mirroring `useSoftphone.ts`'s approach of
 * defining local interfaces rather than relying on JsSIP's own `.d.ts`
 * exports — those don't include `pcConfig` on `UAConfiguration` (it only
 * appears on per-call `CallOptions`/`AnswerOptions`), even though the runtime
 * JsSIP UA constructor accepts it. See task-7-report.md for details.
 */
interface JsSipSession {
  on: (event: string, cb: (...args: unknown[]) => void) => void
  terminate: () => void
  mute: (options?: { audio?: boolean; video?: boolean }) => void
  unmute: (options?: { audio?: boolean; video?: boolean }) => void
  connection?: RTCPeerConnection
}

interface JsSipUaInstance {
  on: (event: string, cb: (...args: unknown[]) => void) => void
  start: () => void
  stop: () => void
  call: (
    target: string,
    opts?: { mediaConstraints?: { audio: boolean; video: boolean }; mediaStream?: MediaStream },
  ) => JsSipSession
}

export interface ClientCallGuestIdentity {
  name: string
  email: string
}

/** The AI voice assistant's verified hand-off (portal voice session + its token). */
export interface ClientCallAiHandoff { sessionId: string; token: string }

export function useClientCall(tenantSlug: string, guestIdentity?: ClientCallGuestIdentity | null, aiHandoff?: ClientCallAiHandoff | null): UseClientCallResult {
  const [state, setState] = useState<ClientCallState>('IDLE')
  const [errorCode, setErrorCode] = useState<ClientCallErrorCode | null>(null)
  const [durationSeconds, setDurationSeconds] = useState(0)
  const [muted, setMuted] = useState(false)
  const [callSessionId, setCallSessionId] = useState<string | null>(null)

  // Synchronous ref mirror of `state` so JsSIP event handlers (which fire
  // outside React's render cycle) can read the current call state without
  // a stale closure. Without this, `ua.on('disconnected')` captures the
  // state at the time the handler was registered, not at the time it fires.
  const stateRef = useRef<ClientCallState>('IDLE')
  useEffect(() => {
    stateRef.current = state
  }, [state])

  const uaRef = useRef<JsSipUaInstance | null>(null)
  const sessionRef = useRef<JsSipSession | null>(null)
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const localStreamRef = useRef<MediaStream | null>(null)
  // Playback element for the agent's audio. JsSIP does not auto-play remote
  // media — useSoftphone.ts (the agent side) attaches received tracks to an
  // <audio> element on 'accepted'/'confirmed' the same way; without this
  // the visitor's call would reach CONNECTED with the timer running and
  // still be completely silent.
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null)
  const widgetSocketRef = useRef<ReturnType<typeof createWidgetSocket> | null>(null)
  // RFC 7118 CRLF keep-alive — same idle-drop protection as useSoftphone.
  const sipKeepaliveRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fail = useCallback((code: ClientCallErrorCode) => {
    setErrorCode(code)
    setState('FAILED')
  }, [])

  const cleanup = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current)
      durationIntervalRef.current = null
    }
    if (sipKeepaliveRef.current) {
      clearInterval(sipKeepaliveRef.current)
      sipKeepaliveRef.current = null
    }
    for (const track of localStreamRef.current?.getTracks() ?? []) track.stop()
    localStreamRef.current = null
    sessionRef.current = null
    uaRef.current?.stop()
    uaRef.current = null
    widgetSocketRef.current?.disconnect()
    widgetSocketRef.current = null
  }, [])

  useEffect(() => {
    return () => {
      sessionRef.current?.terminate()
      cleanup()
    }
  }, [])

  const startCall = useCallback(async () => {
    setErrorCode(null)
    console.log('[kc3-client-call] startCall — tenantSlug:', tenantSlug)

    // NOTE: deliberately does NOT also check `typeof RTCPeerConnection ===
    // 'undefined'` here. jsdom (this repo's test environment, see
    // apps/web/src/__tests__/setup.ts) does not implement RTCPeerConnection
    // and never stubs it — a check on that global made every startCall() in
    // the test suite report UNSUPPORTED_BROWSER regardless of mocked
    // availability/getUserMedia, contradicting the spec'd test outcomes.
    // `useSoftphone.ts` (the existing agent softphone hook) has no such
    // guard either. getUserMedia's own absence is still checked below, which
    // is the actual signal real unsupported browsers hit first.
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      fail('UNSUPPORTED_BROWSER')
      return
    }

    setState('CONNECTING')
    console.log('[kc3-client-call] checking agent availability — GET /portal/' + tenantSlug + '/call/availability')
    // An AI hand-off goes to the AI agent's own hand-off queue, which holds the
    // caller until someone answers — the portal queue's availability is irrelevant.
    let available: { available: boolean } = { available: true }
    if (!aiHandoff) try {
      available = await clientApi.get<{ available: boolean }>(`/portal/${tenantSlug}/call/availability`)
    } catch {
      console.error('[kc3-client-call] availability check failed — WS_UNREACHABLE')
      fail('WS_UNREACHABLE')
      return
    }
    if (!available.available) {
      console.log('[kc3-client-call] no agents available — NO_AGENTS')
      fail('NO_AGENTS')
      return
    }
    console.log('[kc3-client-call] agents available — proceeding')

    setState('REQUESTING_PERMISSION')
    console.log('[kc3-client-call] requesting microphone permission')
    try {
      localStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
    } catch {
      console.error('[kc3-client-call] mic permission denied — MIC_DENIED')
      fail('MIC_DENIED')
      return
    }
    console.log('[kc3-client-call] mic permission granted')

    setState('CONNECTING')
    console.log('[kc3-client-call] creating call session — POST /portal/' + tenantSlug + '/call/session')
    let session: CallSessionResponse
    try {
      session = await clientApi.post<CallSessionResponse>(
        `/portal/${tenantSlug}/call/session`,
        {
          ...(guestIdentity ? { name: guestIdentity.name, email: guestIdentity.email } : {}),
          ...(aiHandoff ? { aiHandoff } : {}),
        },
      )
    } catch (err) {
      const code = callSessionErrorCode(err)
      console.error('[kc3-client-call] call session creation failed —', code, err)
      fail(code)
      return
    }
    console.log('[kc3-client-call] call session created — callSessionId:', session.callSessionId, 'dialTarget:', session.dialTarget)

    // The API can only see the Host header it was proxied with (e.g. Vite's
    // /api proxy rewrites it to the API's own host:port via changeOrigin),
    // never the browser's real origin — so session.wsUrl from the server is
    // unreliable. Build it from window.location instead, same-origin,
    // mirroring the agent softphone's working wsUrl derivation
    // (AgentLayout.tsx).
    //
    // In dev (bare `vite`, no reverse proxy in front), the same-origin
    // /ws-sip path can't be proxied through Vite's own dev server — Vite's
    // dev server intercepts/interferes with WebSocket upgrades on arbitrary
    // paths regardless of proxy config (see vitejs/vite#864, vitejs/vite#4124;
    // confirmed here via a minimal raw-WS echo server — direct connections
    // succeed, the identical request proxied through Vite always closes with
    // code 1006 even though the target receives it correctly). Kamailio's WS
    // listener (port 5062, plain ws) is already published directly by
    // docker-compose for local dev, so connect to it directly instead. In a
    // real deployment (import.meta.env.PROD), nginx/ingress fronts the app
    // and IS able to proxy /ws-sip correctly, so the same-origin path is used
    // there.
    const wsUrl = import.meta.env.DEV
      ? `ws://${window.location.hostname}:5062`
      : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws-sip`
    console.log('[kc3-client-call] using client-derived wsUrl:', wsUrl, '(server suggested:', session.wsUrl, ')')

    console.log('[kc3-client-call] call session created — callSessionId:', session.callSessionId, 'dialTarget:', session.dialTarget)
    setCallSessionId(session.callSessionId)

    // Opens a /widget namespace connection for the DURATION of this ringing
    // attempt (not only reactively after NO_AGENTS, as the existing retry
    // effect below does with a SEPARATE, freshly-minted /chat/session and a
    // DIFFERENT visitorId) — this is what lets a server-pushed 'call:timeout'
    // reach this exact call while it's still ringing. Best-effort: a failed
    // connection here must not block the call itself, since the SIP path is
    // independent of it.
    try {
      widgetSocketRef.current = createWidgetSocket(session.token)
      widgetSocketRef.current.on('connect', () => {
        console.log('[kc3-client-call] widget socket connected — listening for call:ended/call:timeout')
      })
      widgetSocketRef.current.on('connect_error', (err: unknown) => {
        console.error('[kc3-client-call] widget socket connect error:', err)
      })
      widgetSocketRef.current.on('disconnect', (reason: unknown) => {
        console.warn('[kc3-client-call] widget socket disconnected:', reason)
      })
      widgetSocketRef.current.on('call:timeout', () => {
        console.log('[kc3-client-call] queue timeout — no agent answered')
        cleanup()
        fail('TIMEOUT')
      })
      // Server-side fallback for call disconnection: when the agent hangs up,
      // the server publishes call.ended → the ws-gateway relays it here via
      // the /widget namespace. The SIP BYE is the primary mechanism, but it
      // can be unreliable (Kamailio/Asterisk routing, WebSocket transport
      // drops). Without this listener the visitor's widget stays stuck on
      // "Connected" after the agent hangs up, with no audio and no way out
      // but a manual end-call click.
      widgetSocketRef.current.on('call:ended', () => {
        console.log('[kc3-client-call] server notified call:ended — disconnecting')
        cleanup()
        setState('ENDED')
      })
    } catch (err) {
      console.warn('[kc3-client-call] failed to open widget socket for timeout notifications', err)
    }

    const { iceServers } = buildIceServers({ useStun: true })
    const sipSocket = new JsSIP.WebSocketInterface(wsUrl)
    const ua = new JsSIP.UA({
      sockets: [sipSocket],
      uri: `sip:${session.sipUsername}@${session.sipDomain}`,
      password: session.password,
      register: true,
      session_timers: false,
      // `pcConfig` is not in JsSIP's published `UAConfiguration` type (it only
      // appears on per-call `CallOptions`/`AnswerOptions`), even though the
      // runtime UA constructor accepts and applies it — see the discrepancy
      // note above `JsSipUaInstance`. Cast through `unknown` rather than
      // widening the constructor's declared parameter type.
      pcConfig: { iceServers },
    } as unknown as ConstructorParameters<typeof JsSIP.UA>[0]) as unknown as JsSipUaInstance
    uaRef.current = ua

    // A dropped WS mid-call is a dead call — JsSIP ships no keepalive, so a
    // CRLF ping every 30s keeps NAT/proxy/server idle timers from killing an
    // otherwise-healthy registration. See useSoftphone.ts for the full note.
    sipKeepaliveRef.current = setInterval(() => {
      try {
        const sock = sipSocket as unknown as {
          isConnected(): boolean
          send(m: string): boolean
        }
        if (sock.isConnected()) sock.send('\r\n\r\n')
      } catch {
        // teardown race — harmless
      }
    }, 30_000)

    ua.on('connecting', () => {
      console.log('[kc3-client-call] SIP WS transport connecting')
    })
    ua.on('connected', () => {
      console.log('[kc3-client-call] SIP WS transport connected')
    })
    ua.on('disconnected', () => {
      console.error('[kc3-client-call] WS transport disconnected — WS_UNREACHABLE')
      // If the call was CONNECTED and the SIP WS transport drops, the call
      // is effectively dead — the agent likely hung up and Kamailio could
      // not relay the BYE to the browser. Without this, the visitor stays
      // stuck on "Connected" with no audio and no way out but a manual
      // end-call click. The `call:ended` socket event is the primary
      // fallback, but if the widget socket also failed to connect, this
      // is the only signal.
      if (stateRef.current === 'CONNECTED') {
        console.log('[kc3-client-call] WS transport dropped during CONNECTED — ending call')
        cleanup()
        setState('ENDED')
        return
      }
      if (stateRef.current === 'ENDED' || stateRef.current === 'FAILED') return
      fail('WS_UNREACHABLE')
    })
    ua.on('registrationFailed', () => {
      console.error('[kc3-client-call] SIP registration failed — REGISTER_FAILED')
      fail('REGISTER_FAILED')
    })
    ua.on('registered', () => {
      console.log('[kc3-client-call] SIP registered — dialing agent at sip:' + session.dialTarget + '@' + session.sipDomain)
      const outboundSession = ua.call(`sip:${session.dialTarget}@${session.sipDomain}`, {
        mediaConstraints: { audio: true, video: false },
        ...(localStreamRef.current ? { mediaStream: localStreamRef.current } : {}),
      })
      sessionRef.current = outboundSession

      outboundSession.on('progress', () => {
        console.log('[kc3-client-call] call progress — RINGING')
        setState('RINGING')
      })
      // Diagnostics for "call connects but no audio" — added to see exactly
      // where the media path breaks: whether the agent's SDP answer ever
      // arrives, whether ICE actually connects (vs. just SDP negotiation
      // succeeding while no candidate pair works — TURN/STUN misconfig or a
      // one-way NAT would show exactly this split), and whether remote
      // tracks are received at all before assuming they are.
      outboundSession.on('peerconnection', (...args: unknown[]) => {
        const e = args[0] as { peerconnection?: RTCPeerConnection } | undefined
        const pc = e?.peerconnection
        console.log('[kc3-client-call][webrtc] peerconnection created')
        if (!pc) return
        pc.addEventListener('iceconnectionstatechange', () => {
          console.log('[kc3-client-call][webrtc] iceConnectionState:', pc.iceConnectionState)
        })
        pc.addEventListener('connectionstatechange', () => {
          console.log('[kc3-client-call][webrtc] connectionState:', pc.connectionState)
        })
        pc.addEventListener('icegatheringstatechange', () => {
          console.log('[kc3-client-call][webrtc] iceGatheringState:', pc.iceGatheringState)
        })
        pc.addEventListener('icecandidate', (ev) => {
          if (ev.candidate) {
            console.log('[kc3-client-call][webrtc] local ICE candidate:', ev.candidate.type, ev.candidate.protocol, ev.candidate.address ?? ev.candidate.candidate)
          } else {
            console.log('[kc3-client-call][webrtc] local ICE candidate gathering complete')
          }
        })
        pc.addEventListener('track', (ev) => {
          console.log('[kc3-client-call][webrtc] remote track received:', ev.track.kind, 'readyState:', ev.track.readyState, 'muted:', ev.track.muted)
        })
      })
      // TSV-03-0008 — JsSIP only terminates on iceConnectionState='failed',
      // which never fires when ZERO candidates were gathered (no pairs to
      // check -> state sits at 'new' and the SIP dialog completes with a
      // dead media path). For an outbound session the RTCPeerConnection
      // already exists here — JsSIP emits 'peerconnection' synchronously
      // inside ua.call() before these handlers attach — so the guard is
      // bound via session.connection, not the event.
      {
        const pc = outboundSession.connection
        if (pc) {
          attachIceFailureGuard(outboundSession, pc, {
            onTerminate: (reason) => {
              console.error('[kc3-client-call][webrtc] ICE failure — terminating call:', reason)
              fail('UNEXPECTED_TERMINATION')
            },
          })
        }
      }
      outboundSession.on('sdp', (...args: unknown[]) => {
        const e = args[0] as { originator?: string; type?: string; sdp?: string } | undefined
        const audioLine = e?.sdp?.split('\r\n').find((l) => l.startsWith('m=audio'))
        console.log('[kc3-client-call][webrtc] sdp', e?.originator, e?.type, '— m=audio line:', audioLine)
      })
      // 'confirmed' (fires once the ACK completes the 3-way handshake) is a
      // fallback for 'accepted' (fires on the 200 OK, before ACK) — some
      // browsers/paths don't reliably deliver 'accepted' to the app layer
      // even though the call is genuinely up, which left the widget stuck
      // on "Connecting to agent…" with no error and no way out but a
      // manual hangup. useSoftphone.ts (the agent side) has the same
      // fallback for the identical reason. markConnected is idempotent —
      // guards against starting the duration interval twice if BOTH events
      // fire, which is the common case.
      let connected = false
      const markConnected = () => {
        if (connected) return
        connected = true
        setState('CONNECTED')
        durationIntervalRef.current = setInterval(() => setDurationSeconds((s) => s + 1), 1000)

        const peerConnection = outboundSession.connection
        if (peerConnection && remoteAudioRef.current && !remoteAudioRef.current.srcObject) {
          const remoteStream = new MediaStream()
          peerConnection.getReceivers().forEach((receiver) => {
            if (receiver.track) remoteStream.addTrack(receiver.track)
          })
          remoteAudioRef.current.srcObject = remoteStream
          console.log(
            '[kc3-client-call] remote audio stream attached — tracks:',
            remoteStream.getTracks().map((t) => `${t.kind}:${t.readyState}`),
          )
        }

        // One-shot getStats() dump 3s after connect — long enough for ICE to
        // settle. The selected candidate pair's local/remote types (host /
        // srflx / relay) is the single most direct signal for "no audio":
        // relay-relay or srflx-srflx means TURN/STUN did their job; anything
        // failing to produce a succeeded pair at all means ICE never
        // actually connected despite SDP negotiation completing.
        if (peerConnection) {
          setTimeout(() => {
            void peerConnection.getStats().then((stats) => {
              let selectedPairId: string | undefined
              stats.forEach((report) => {
                if (report.type === 'transport' && typeof report.selectedCandidatePairId === 'string') {
                  selectedPairId = report.selectedCandidatePairId
                }
              })
              if (!selectedPairId) {
                console.warn('[kc3-client-call][webrtc] getStats: no selected candidate pair — ICE never connected')
                return
              }
              const pair = stats.get(selectedPairId) as
                | { localCandidateId?: string; remoteCandidateId?: string; state?: string; bytesReceived?: number; bytesSent?: number }
                | undefined
              const local = pair?.localCandidateId ? (stats.get(pair.localCandidateId) as { candidateType?: string } | undefined) : undefined
              const remote = pair?.remoteCandidateId ? (stats.get(pair.remoteCandidateId) as { candidateType?: string } | undefined) : undefined
              console.log('[kc3-client-call][webrtc] getStats: selected pair', {
                state: pair?.state,
                localType: local?.candidateType,
                remoteType: remote?.candidateType,
                bytesReceived: pair?.bytesReceived,
                bytesSent: pair?.bytesSent,
              })
              if ((pair?.bytesReceived ?? 0) === 0) {
                console.warn('[kc3-client-call][webrtc] getStats: 0 bytes received on the selected pair — ICE connected but no inbound RTP is arriving')
              }
            })
          }, 3000)
        }
      }
      outboundSession.on('accepted', () => {
        console.log('[kc3-client-call] call accepted — CONNECTED')
        markConnected()
      })
      outboundSession.on('confirmed', () => {
        console.log('[kc3-client-call] call confirmed — CONNECTED')
        markConnected()
      })
      outboundSession.on('failed', (...args: unknown[]) => {
        const e = args[0] as { cause?: string; message?: { status_code?: number; reason_phrase?: string } } | undefined
        console.error(
          '[kc3-client-call] call failed — cause:', e?.cause,
          'status:', e?.message?.status_code, e?.message?.reason_phrase,
        )
        cleanup()
        if (e?.cause === 'Rejected' || e?.cause === 'Busy') fail('AGENT_REJECTED')
        else if (e?.cause === 'Request Timeout' || e?.cause === 'No Answer') fail('TIMEOUT')
        else fail('UNEXPECTED_TERMINATION')
      })
      outboundSession.on('ended', () => {
        console.log('[kc3-client-call] call ended normally — ENDED')
        cleanup()
        setState('ENDED')
      })
      // JsSIP fires `terminated` for ANY session termination (BYE received,
      // failed, rejected, etc.) — it's the catch-all that fires after
      // `ended` or `failed`. If neither `ended` nor `failed` fired (e.g.
      // the SIP transport dropped without a BYE), this is the last-resort
      // signal that the call is over.
      outboundSession.on('terminated', () => {
        console.log('[kc3-client-call] session terminated — ensuring cleanup')
        if (stateRef.current !== 'ENDED' && stateRef.current !== 'FAILED') {
          cleanup()
          setState('ENDED')
        }
      })
    })

    ua.start()
  }, [tenantSlug, fail, cleanup, guestIdentity])

  const endCall = useCallback(() => {
    setState('ENDING')
    sessionRef.current?.terminate()
    cleanup()
    setState('ENDED')
  }, [cleanup])

  const toggleMute = useCallback(() => {
    const session = sessionRef.current
    if (!session) return
    if (muted) {
      session.unmute({ audio: true })
      console.log('[kc3-client-call] audio unmuted')
    } else {
      session.mute({ audio: true })
      console.log('[kc3-client-call] audio muted')
    }
    setMuted((m) => !m)
  }, [muted])

  const retry = useCallback(async () => {
    setState('IDLE')
    setErrorCode(null)
    setDurationSeconds(0)
    await startCall()
  }, [startCall])

  // While stuck on NO_AGENTS, listen for a live availability push instead of
  // making the visitor manually hit "Try Again" — see
  // docs/superpowers/specs/2026-08-13-client-portal-call-support-webrtc-fix-design.md.
  useEffect(() => {
    if (state !== 'FAILED' || errorCode !== 'NO_AGENTS') return

    let cancelled = false
    let socket: ReturnType<typeof createWidgetSocket> | null = null

    void (async () => {
      try {
        const session = await clientApi.post<{ token: string; visitorId: string }>(`/portal/${tenantSlug}/chat/session`, {})
        if (cancelled) return
        socket = createWidgetSocket(session.token)
        socket.on('widget:availability:changed', (data: { available: boolean }) => {
          if (!cancelled && data.available) {
            void retry()
          }
        })
      } catch {
        // No live updates available (e.g. session mint failed) — the visitor
        // can still fall back to the existing "Try Again" button.
      }
    })()

    return () => {
      cancelled = true
      socket?.disconnect()
    }
  }, [state, errorCode, tenantSlug, retry])

  return { state, errorCode, durationSeconds, muted, remoteAudioRef, callSessionId, startCall, endCall, toggleMute, retry }
}
